import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Search, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocationSelectContent } from './LocationSelectContent';
import { DateSelectContent } from './DateSelectContent';
import { useLocationStore } from '@/stores/useLocationStore';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface MobileSearchOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    onSearch: () => void;
}

type Step = 'location' | 'dates';

/**
 * MobileSearchOverlay Component
 *
 * Full-screen mobile search experience.
 * Location picker + date picker (always visible, with clear button).
 * No rental mode toggle — users browse all products and filter on the catalogue.
 */
export const MobileSearchOverlay = ({ isOpen, onClose, onSearch }: MobileSearchOverlayProps) => {
    const [activeStep, setActiveStep] = useState<Step>('location');
    const { location, dateRange, dateMode, reset } = useLocationStore();

    const handleSearch = () => {
        onSearch();
        onClose();
    };

    // Valid if location is set
    const isValid = !!location;

    const getSearchLabel = () => {
        const hasDates = dateMode === 'fixed' && dateRange.from && dateRange.to;
        return hasDates ? 'Search' : 'Browse catalogue';
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/35" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center text-center sm:items-center sm:p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="flex h-[90vh] w-full max-w-md transform flex-col overflow-hidden bg-muted text-left align-middle shadow-xl transition-[opacity,transform] sm:h-auto sm:rounded-2xl">
                                {/* Header */}
                                <div className="flex items-center justify-between p-4 border-b border-border bg-white">
                                    <button
                                        onClick={onClose}
                                        className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                        aria-label="Close search"
                                        type="button"
                                    >
                                        <X className="h-5 w-5 text-muted-foreground" />
                                    </button>
                                    <Dialog.Title as="h2" className="text-lg font-semibold text-foreground">
                                        Find equipment
                                    </Dialog.Title>
                                    <div className="w-11" aria-hidden="true" /> {/* Spacer for centering */}
                                </div>

                                {/* Content */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-4">

                                    {/* Location Card */}
                                    <div className={cn(
                                        "overflow-hidden rounded-xl border bg-white shadow-sm transition-[border-color,box-shadow]",
                                        activeStep === 'location' ? "ring-2 ring-ring border-transparent" : "border-border"
                                    )}>
                                        <button
                                            className="w-full flex items-center justify-between p-4 text-left"
                                            onClick={() => setActiveStep('location')}
                                            aria-describedby={activeStep === 'location' ? 'mobile-pickup-location-help' : undefined}
                                        >
                                            <div className="flex items-center gap-2">
                                                <MapPin className="h-4 w-4 text-primary" />
                                                <span className="text-sm font-medium text-muted-foreground">Pickup Location</span>
                                            </div>
                                            <span className="text-sm font-semibold text-foreground">
                                                {location ? location.name : "Select location"}
                                            </span>
                                        </button>

                                        {activeStep === 'location' && (
                                            <div className="px-2 pb-4 animate-in slide-in-from-top-2 fade-in duration-200">
                                                {/* Helper text */}
                                                <p id="mobile-pickup-location-help" className="text-xs text-muted-foreground px-2 mb-3">
                                                    Choose where you&apos;ll collect and return your items.
                                                </p>
                                                <LocationSelectContent onSelect={() => setActiveStep('dates')} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Dates Card */}
                                    <div className={cn(
                                        "overflow-hidden rounded-xl border bg-white shadow-sm transition-[border-color,box-shadow]",
                                        activeStep === 'dates' ? "ring-2 ring-primary border-transparent" : "border-border"
                                    )}>
                                        <button
                                            className="w-full flex items-center justify-between p-4 text-left"
                                            onClick={() => setActiveStep('dates')}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Calendar className="h-4 w-4 text-primary" />
                                                <span className="text-sm font-medium text-muted-foreground">When</span>
                                            </div>
                                            <span className="text-sm font-semibold text-foreground">
                                                {dateRange.from ? (
                                                    <>
                                                        {format(dateRange.from, 'MMM d')}
                                                        {dateRange.to ? ` - ${format(dateRange.to, 'MMM d')}` : ''}
                                                    </>
                                                ) : "Optional — add dates"}
                                            </span>
                                        </button>

                                        {activeStep === 'dates' && (
                                            <div className="px-2 pb-2 animate-in slide-in-from-top-2 fade-in duration-200">
                                                <p className="text-xs text-muted-foreground px-2 mb-3">
                                                    Adding dates shows availability and pricing. You can also browse without dates.
                                                </p>
                                                <DateSelectContent />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="p-4 border-t border-border bg-white">
                                    <div className="flex items-center justify-between">
                                        <button
                                            className="text-sm font-medium text-muted-foreground underline"
                                            onClick={() => {
                                                reset();
                                                setActiveStep('location');
                                            }}
                                            type="button"
                                        >
                                            Clear all
                                        </button>

                                        <Button
                                            size="lg"
                                            className="gap-2 px-8"
                                            onClick={handleSearch}
                                            disabled={!isValid}
                                        >
                                            <Search className="h-4 w-4" />
                                            {getSearchLabel()}
                                        </Button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
